<template>
  <a-drawer
    :title="title"
    width="60%"
    :visible="visible"
    :body-style="{ paddingBottom: '80px' }"
    @close="onClose"
  >
    <a-spin :spinning="loading">
      <div class="drawer-content">
        <!-- 基本信息 -->
        <a-descriptions title="基本信息" :column="2" bordered>
          <a-descriptions-item label="订单ID">{{ detailData.id || '-' }}</a-descriptions-item>
          <a-descriptions-item label="订单编号">{{ detailData.orderNo || '-' }}</a-descriptions-item>
          <a-descriptions-item label="支付单号">{{ detailData.payOrderNo || '-' }}</a-descriptions-item>
          <a-descriptions-item label="套餐ID">{{ detailData.packageId || '-' }}</a-descriptions-item>
          <a-descriptions-item label="购买短信数量">{{ detailData.smsCount ? `${detailData.smsCount}条` : '-' }}</a-descriptions-item>
          <a-descriptions-item label="支付渠道">
            <a-tag :color="getPayMethodColor(detailData.payMethod)">
              {{ getPayMethodText(detailData.payMethod) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="支付类型">
            <a-tag :color="getPayTypeColor(detailData.payType)">
              {{ getPayTypeText(detailData.payType) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="支付状态">
            <a-tag :color="getPayStatusColor(detailData.payStatus)">
              {{ getPayStatusText(detailData.payStatus) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="订单状态">
            <a-tag :color="getOrderStatusColor(detailData.status)">
              {{ getOrderStatusText(detailData.status) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="退款状态">
            <a-tag :color="getRefundStatusColor(detailData.refundStatus)">
              {{ getRefundStatusText(detailData.refundStatus) }}
            </a-tag>
          </a-descriptions-item>
        </a-descriptions>

        <!-- 金额信息 -->
        <a-descriptions title="金额信息" :column="2" bordered style="margin-top: 16px">
          <a-descriptions-item label="套餐原价">
            <span class="amount-value">¥{{ formatAmount(detailData.price) }}</span>
          </a-descriptions-item>
          <a-descriptions-item label="实际支付金额">
            <span class="amount-value actual">¥{{ formatAmount(detailData.actualPayAmount) }}</span>
          </a-descriptions-item>
        </a-descriptions>

        <!-- 时间信息 -->
        <a-descriptions title="时间信息" :column="2" bordered style="margin-top: 16px">
          <a-descriptions-item label="创建时间">{{ formatTime(detailData.createTime) }}</a-descriptions-item>
          <a-descriptions-item label="支付时间">{{ formatTime(detailData.payTime) }}</a-descriptions-item>
        </a-descriptions>

        <!-- 其他信息 -->
        <a-descriptions title="其他信息" :column="1" bordered style="margin-top: 16px">
          <a-descriptions-item label="三方支付凭证">{{ detailData.payThreeVoucher || '无' }}</a-descriptions-item>
          <a-descriptions-item label="三方退款凭证">{{ detailData.refundThreeVoucher || '无' }}</a-descriptions-item>
          <a-descriptions-item label="备注">{{ detailData.remark || '无备注' }}</a-descriptions-item>
        </a-descriptions>
      </div>
    </a-spin>

    <div class="drawer-footer">
      <a-button @click="onClose">关闭</a-button>
    </div>
  </a-drawer>
</template>

<script>
import { getObj } from '@/api/sms/order'
import moment from 'moment'

export default {
  name: 'DetailDrawer',
  data() {
    return {
      visible: false,
      loading: false,
      title: '订单详情',
      detailData: {},
    }
  },
  methods: {
    show(record) {
      this.visible = true
      this.title = `订单详情`
      this.loadDetail(record.id)
    },

    hide() {
      this.visible = false
      this.detailData = {}
    },

    onClose() {
      this.hide()
    },

    async loadDetail(id) {
      this.loading = true
      try {
        const response = await getObj(id)
        if (response.success) {
          this.detailData = response.data || {}
        } else {
          this.$message.error('加载详情失败')
        }
      } catch (error) {
        console.error('加载订单详情失败:', error)
        this.$message.error('加载订单详情失败')
      } finally {
        this.loading = false
      }
    },

    // 格式化金额显示
    formatAmount(amount) {
      if (!amount && amount !== 0) return '0.00'
      return parseFloat(amount).toFixed(2)
    },

    // 格式化时间显示
    formatTime(time) {
      if (!time) return '-'
      return moment(time).format('YYYY-MM-DD HH:mm:ss')
    },

    // 支付渠道相关方法
    getPayMethodText(method) {
      const methodMap = {
        '0': '线上支付',
        '1': '线下支付',
        '2': '企业转账',
      }
      return methodMap[method] || '-'
    },

    getPayMethodColor(method) {
      const colorMap = { '0': 'blue', '1': 'green', '2': 'orange' }
      return colorMap[method] || 'default'
    },

    // 支付类型相关方法
    getPayTypeText(type) {
      const typeMap = {
        'ALIPAY': '支付宝',
        'WECHAT': '微信',
        'UNIONPAY': '银联',
        'BANK': '银行转账',
      }
      return typeMap[type] || '-'
    },

    getPayTypeColor(type) {
      const colorMap = { 'ALIPAY': 'blue', 'WECHAT': 'green', 'UNIONPAY': 'orange', 'BANK': 'purple' }
      return colorMap[type] || 'default'
    },

    // 支付状态相关方法
    getPayStatusText(status) {
      const statusMap = { 0: '待支付', 1: '已支付' }
      return statusMap[status] || '-'
    },

    getPayStatusColor(status) {
      const colorMap = { 0: 'orange', 1: 'green' }
      return colorMap[status] || 'default'
    },

    // 订单状态相关方法
    getOrderStatusText(status) {
      const statusMap = {
        0: '待支付',
        6: '订单完成',
        7: '订单取消',
        8: '已退款',
      }
      return statusMap[status] || '-'
    },

    getOrderStatusColor(status) {
      const colorMap = { 0: 'orange', 6: 'green', 7: 'red', 8: 'blue' }
      return colorMap[status] || 'default'
    },

    // 退款状态相关方法
    getRefundStatusText(status) {
      const statusMap = {
        0: '未退款',
        1: '退款中',
        2: '已退款',
        3: '退款失败',
      }
      return statusMap[status] || '-'
    },

    getRefundStatusColor(status) {
      const colorMap = { 0: 'grey', 1: 'orange', 2: 'green', 3: 'red' }
      return colorMap[status] || 'default'
    },
  },
}
</script>

<style scoped>
.drawer-content {
  padding: 0;
}

.drawer-footer {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 100%;
  border-top: 1px solid #e9e9e9;
  padding: 10px 16px;
  background: #fff;
  text-align: right;
  z-index: 1;
}

.ant-descriptions {
  margin-bottom: 0;
}

/* 确保label不换行 */
.ant-descriptions-item-label {
  white-space: nowrap;
  min-width: 90px;
  text-align: right;
  padding-right: 8px;
}

.amount-value {
  font-weight: 600;
  color: #1890ff;
}

.amount-value.actual {
  color: #52c41a;
  font-size: 16px;
}

</style>
